/**
 * BSCScan API Service
 * Fetches token transfer data from BSCScan for USD.P token
 */

import { getTokenConfig } from '../config/tokens';

export interface TokenTransfer {
  blockNumber: string;
  timeStamp: string;
  hash: string;
  nonce: string;
  blockHash: string;
  from: string;
  contractAddress: string;
  to: string;
  value: string;
  tokenName: string;
  tokenSymbol: string;
  tokenDecimal: string;
  transactionIndex: string;
  gas: string;
  gasPrice: string;
  gasUsed: string;
  cumulativeGasUsed: string;
  input: string;
  confirmations: string;
}

export interface FormattedTransfer {
  id: string;
  txHash: string;
  from: string;
  to: string;
  amount: string;
  tokenSymbol: string;
  tokenName: string;
  tokenLogo: string;
  blockNumber: number;
  timestamp: number;
  gasUsed: string;
  gasPrice: string;
  status: 'confirmed' | 'pending' | 'failed';
  chainId: number;
  networkName: string;
  type: 'transfer';
}

class BSCScanService {
  private static instance: BSCScanService;
  private baseUrl = 'https://api.bscscan.com/api'; // Standard BSCScan API
  private chainId = 56; // BSC Mainnet
  private apiKey = 'YourApiKeyToken'; // Default demo key (rate limited). Get a free key at https://bscscan.com/myapikey
  private usdpContractAddress = '0x5bC001A73Db235D9e02606B7C53a1490f864d0B8';
  private cache: Map<string, { data: FormattedTransfer[]; timestamp: number }> = new Map();
  private cacheDuration = 30000; // 30 seconds cache
  private lastError: string | null = null;

  static getInstance(): BSCScanService {
    if (!this.instance) {
      this.instance = new BSCScanService();
    }
    return this.instance;
  }

  private constructor() {
    console.log('🔧 BSCScanService initialized');
  }

  /**
   * Fetch token transfers from BSCScan API
   */
  async fetchTokenTransfers(
    contractAddress: string = this.usdpContractAddress,
    page: number = 1,
    offset: number = 100,
    sort: 'asc' | 'desc' = 'desc'
  ): Promise<TokenTransfer[]> {
    const cacheKey = `${contractAddress}_${page}_${offset}_${sort}`;
    
    // Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      console.log('📦 Returning cached BSCScan data');
      return this.parseTransfersFromCache(cached.data);
    }

    try {
      // Note: chainid is not required for api.bscscan.com
      const url = `${this.baseUrl}?module=account&action=tokentx&contractaddress=${contractAddress}&page=${page}&offset=${offset}&sort=${sort}&apikey=${this.apiKey}`;
      
      console.log('🌐 Fetching token transfers from BSCScan API...');
      console.log('📍 URL:', url.replace(this.apiKey, 'API_KEY_HIDDEN'));
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 BSCScan API Response:', {
        status: data.status,
        message: data.message,
        result: data.result,
        resultCount: Array.isArray(data.result) ? data.result.length : 0
      });

      if (data.status === '1' && data.result) {
        console.log(`✅ Fetched ${data.result.length} token transfers from BSCScan`);
        this.lastError = null;
        return data.result;
      } else if (data.status === '0' && data.message === 'No transactions found') {
        console.log('ℹ️ No transactions found for this token');
        this.lastError = null;
        return [];
      } else {
        const errorMsg = `BSCScan API error: ${data.message || 'Unknown error'}`;
        console.error('❌', errorMsg);
        console.error('❌ Full API Response:', data);
        
        // Detect specific error types
        if (data.message && data.message.includes('temporarily unavailable')) {
          console.warn('⚠️ BSCScan free API is temporarily unavailable due to high network activity');
          console.warn('⚠️ Using demo data as fallback. API will retry automatically.');
          this.lastError = `API Temporarily Unavailable: ${data.message}`;
        } else if (data.message && (data.message.includes('Invalid API Key') || data.message === 'NOTOK')) {
          console.error('⚠️ API Key Issue Detected!');
          console.error('⚠️ Please get a valid BSCScan API key from: https://bscscan.com/myapikey');
          console.error('⚠️ Then update src/services/bscScanService.ts line 52');
          this.lastError = `API Key Error: ${data.message}`;
        } else {
          this.lastError = errorMsg;
        }
        return [];
      }
    } catch (error) {
      const errorMsg = `Failed to fetch from BSCScan: ${error instanceof Error ? error.message : String(error)}`;
      console.error('❌', errorMsg);
      this.lastError = errorMsg;
      return [];
    }
  }

  /**
   * Parse raw transfers from cached formatted data
   */
  private parseTransfersFromCache(formattedTransfers: FormattedTransfer[]): TokenTransfer[] {
    return formattedTransfers.map(ft => ({
      blockNumber: ft.blockNumber.toString(),
      timeStamp: Math.floor(ft.timestamp / 1000).toString(),
      hash: ft.txHash,
      nonce: '0',
      blockHash: '',
      from: ft.from,
      contractAddress: this.usdpContractAddress,
      to: ft.to,
      value: ft.amount,
      tokenName: ft.tokenName,
      tokenSymbol: ft.tokenSymbol,
      tokenDecimal: '18',
      transactionIndex: '0',
      gas: '0',
      gasPrice: ft.gasPrice,
      gasUsed: ft.gasUsed,
      cumulativeGasUsed: '0',
      input: '',
      confirmations: '100'
    }));
  }

  /**
   * Format transfers to match our transaction structure
   */
  formatTransfers(transfers: TokenTransfer[]): FormattedTransfer[] {
    const tokenConfig = getTokenConfig('USDP');
    
    return transfers.map(transfer => {
      const amount = this.formatTokenAmount(transfer.value, parseInt(transfer.tokenDecimal));
      
      return {
        id: `bsc_${transfer.hash}_${transfer.transactionIndex}`,
        txHash: transfer.hash,
        from: transfer.from,
        to: transfer.to,
        amount: amount,
        tokenSymbol: transfer.tokenSymbol,
        tokenName: transfer.tokenName,
        tokenLogo: tokenConfig?.logoUrl || '/USDPlogo.png',
        blockNumber: parseInt(transfer.blockNumber),
        timestamp: parseInt(transfer.timeStamp) * 1000, // Convert to milliseconds
        gasUsed: transfer.gasUsed,
        gasPrice: this.formatGwei(transfer.gasPrice),
        status: parseInt(transfer.confirmations) > 0 ? 'confirmed' : 'pending',
        chainId: 56, // BSC Mainnet
        networkName: 'BNB Smart Chain',
        type: 'transfer'
      };
    });
  }

  /**
   * Format token amount from wei to readable format
   */
  private formatTokenAmount(value: string, decimals: number): string {
    const amount = parseFloat(value) / Math.pow(10, decimals);
    return amount.toFixed(6);
  }

  /**
   * Format gas price from wei to gwei
   */
  private formatGwei(weiValue: string): string {
    const gwei = parseFloat(weiValue) / 1e9;
    return gwei.toFixed(4);
  }

  /**
   * Get USD.P token transfers with caching
   */
  async getUSDPTransfers(page: number = 1, limit: number = 100, forceRefresh: boolean = false): Promise<FormattedTransfer[]> {
    const cacheKey = `usdp_${page}_${limit}`;
    
    // Check cache (skip if force refresh)
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
        console.log('📦 Returning cached USDP transfers');
        return cached.data;
      }
    } else {
      console.log('🔄 Force refresh: bypassing cache');
    }

    const rawTransfers = await this.fetchTokenTransfers(
      this.usdpContractAddress,
      page,
      limit,
      'desc'
    );
    
    const formattedTransfers = this.formatTransfers(rawTransfers);
    
    // Update cache
    this.cache.set(cacheKey, {
      data: formattedTransfers,
      timestamp: Date.now()
    });
    
    console.log(`💾 Cached ${formattedTransfers.length} transfers from Etherscan V2 API`);
    return formattedTransfers;
  }

  /**
   * Get transfers for a specific address
   */
  async getTransfersByAddress(
    address: string,
    page: number = 1,
    limit: number = 100
  ): Promise<FormattedTransfer[]> {
    const allTransfers = await this.getUSDPTransfers(page, limit);
    
    return allTransfers.filter(
      transfer =>
        transfer.from.toLowerCase() === address.toLowerCase() ||
        transfer.to.toLowerCase() === address.toLowerCase()
    );
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🗑️ BSCScan cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Get last error message
   */
  getLastError(): string | null {
    return this.lastError;
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<{ success: boolean; message: string; sampleData?: any; error?: string }> {
    try {
      console.log('🧪 Testing BSCScan API connection...');
      const transfers = await this.fetchTokenTransfers(this.usdpContractAddress, 1, 10);
      
      if (transfers.length > 0) {
        return {
          success: true,
          message: `Successfully fetched ${transfers.length} transfers`,
          sampleData: transfers[0]
        };
      } else {
        const lastError = this.getLastError();
        if (lastError) {
          return {
            success: false,
            message: 'API request failed',
            error: lastError
          };
        }
        return {
          success: true,
          message: 'API connection successful but no transfers found for this contract',
          sampleData: null
        };
      }
    } catch (error) {
      return {
        success: false,
        message: `API connection failed: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  /**
   * Generate demo data for testing (when API key is not available)
   */
  generateDemoTransfers(count: number = 10): FormattedTransfer[] {
    console.log('📝 Generating demo transfer data for testing...');
    const demos: FormattedTransfer[] = [];
    const now = Date.now();
    
    for (let i = 0; i < count; i++) {
      demos.push({
        id: `demo_${i}_${now}`,
        txHash: `0x${Math.random().toString(16).substring(2, 66)}`,
        from: `0x${Math.random().toString(16).substring(2, 42)}`,
        to: `0x${Math.random().toString(16).substring(2, 42)}`,
        amount: (Math.random() * 10000 + 100).toFixed(6),
        tokenSymbol: 'USDP',
        tokenName: 'USD.P E-Pay Dollar',
        tokenLogo: '/USDPlogo.png',
        blockNumber: Math.floor(Math.random() * 1000000) + 30000000,
        timestamp: now - (i * 3600000), // 1 hour apart
        gasUsed: (21000 + Math.floor(Math.random() * 30000)).toString(),
        gasPrice: (3 + Math.random() * 2).toFixed(4),
        status: 'confirmed' as const,
        chainId: 56,
        networkName: 'BNB Smart Chain',
        type: 'transfer' as const
      });
    }
    
    return demos;
  }
}

// Export singleton instance
export const bscScanService = BSCScanService.getInstance();